import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star, Search, MapPin, Filter, ArrowLeft, Users, Grid, List, MessageSquare, CheckCircle } from "lucide-react";
import AdvertisementBanner from "@/components/AdvertisementBanner";
import WhatsAppSupport from "@/components/WhatsAppSupport";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_talentmeet-app/artifacts/w1exeb5m_IMG-20260127-WA0008.jpg";

// Default categories (will be merged with dynamic ones)
const DEFAULT_CATEGORIES = [
  "Actor", "Model", "Singer", "Dancer", "Voice Artist", 
  "Musician", "Comedian", "Host/Anchor", "Influencer", "Other"
];

export default function BrowseTalents() {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [viewMode, setViewMode] = useState("grid");
  const [total, setTotal] = useState(0);
  const [dynamicFilters, setDynamicFilters] = useState({ categories: [], skills: [], locations: [] });
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    fetchDynamicFilters();
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [selectedCategory, searchTerm]);

  const fetchDynamicFilters = async () => {
    try {
      const response = await fetch(`${API_URL}/api/filters/dynamic`);
      if (response.ok) {
        const data = await response.json();
        setDynamicFilters(data);
      }
    } catch (error) {
      console.error("Error fetching filters:", error);
    }
  };

  const fetchProfiles = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/profiles?limit=100`;  // Show all profiles
      if (selectedCategory !== "All") {
        url += `&category=${encodeURIComponent(selectedCategory)}`;
      }
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setProfiles(data.profiles);
        setTotal(data.total);
      }
    } catch (error) {
      console.error("Error fetching profiles:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = async (value) => {
    setSearchTerm(value);
    if (value.length >= 2) {
      try {
        const response = await fetch(`${API_URL}/api/search/autocomplete?q=${encodeURIComponent(value)}`);
        if (response.ok) {
          const data = await response.json();
          setSuggestions(data.suggestions || []);
          setShowSuggestions(true);
        }
      } catch (error) {
        console.error("Error fetching suggestions:", error);
      }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion);
    setShowSuggestions(false);
    fetchProfiles();
  };

  // Merge default and dynamic categories
  const allCategories = ["All", ...new Set([...DEFAULT_CATEGORIES, ...dynamicFilters.categories])];

  // No client-side filtering needed - API handles it
  const displayProfiles = profiles;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Announcement Bar */}
      <AdvertisementBanner placement="discovery" format="top_bar" />
      
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-home-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <img src={LOGO_URL} alt="TalentMeet" className="h-8 w-8 rounded-lg object-cover" />
                <span className="font-display text-xl font-bold text-gray-900 hidden sm:block">TalentMeet</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                onClick={() => navigate("/login")}
                data-testid="login-btn"
              >
                Sign In
              </Button>
              <Button 
                className="btn-primary hidden sm:flex"
                onClick={() => navigate("/register")}
                data-testid="register-btn"
              >
                Join Now
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-violet-600 to-pink-600 text-white py-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="font-display text-3xl sm:text-4xl font-bold mb-4">
            Discover Amazing Talent
          </h1>
          <p className="text-violet-100 max-w-2xl mx-auto mb-8">
            Browse through our curated collection of talented actors, models, singers, 
            dancers, and more. Find the perfect talent for your next project.
          </p>

          {/* Search Bar with Autocomplete */}
          <div className="max-w-2xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
            <Input
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              placeholder="Search by name, category, location, or skills..."
              className="pl-12 h-14 text-lg bg-white text-gray-900 border-0 shadow-lg"
              data-testid="search-input"
            />
            {/* Autocomplete Dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-y-auto z-50">
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    className="w-full px-4 py-2 text-left text-gray-900 hover:bg-violet-50 transition-colors"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-gray-100 py-4 px-4 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4 w-full sm:w-auto">
            <Filter className="w-5 h-5 text-gray-500" />
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full sm:w-48" data-testid="category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {allCategories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">
              {displayProfiles.length} talent{displayProfiles.length !== 1 ? 's' : ''} found
            </span>
            <div className="flex border rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 ${viewMode === "grid" ? 'bg-violet-100 text-violet-600' : 'bg-white text-gray-500'}`}
                data-testid="grid-view-btn"
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 ${viewMode === "list" ? 'bg-violet-100 text-violet-600' : 'bg-white text-gray-500'}`}
                data-testid="list-view-btn"
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Profiles Grid/List */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="spinner"></div>
          </div>
        ) : displayProfiles.length === 0 ? (
          <div className="text-center py-20">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No talents found</h3>
            <p className="text-gray-500">Try adjusting your search or filters</p>
          </div>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {displayProfiles.map(profile => {
              const personal = profile.personal_info || {};
              const professional = profile.professional_info || {};
              const portfolio = profile.portfolio || {};
              const profilePhoto = portfolio.profile_photo || (portfolio.additional_photos || [])[0] || (profile.photos || [])[0];
              const displayName = personal.display_name || personal.full_name || profile.name;
              const category = professional.primary_category || profile.category;
              const location = personal.city ? `${personal.city}${personal.country ? `, ${personal.country}` : ''}` : profile.location;
              const skills = professional.skills || profile.skills || [];
              
              return (
              <Card 
                key={profile.profile_id} 
                className="profile-card overflow-hidden hover:shadow-lg transition-shadow"
                data-testid={`profile-card-${profile.profile_id}`}
              >
                <div className="relative cursor-pointer" onClick={() => navigate(`/profile/${profile.profile_id}`)}>
                  <div className="talent-photo-container">
                    {profilePhoto ? (
                      <img 
                        src={profilePhoto} 
                        alt={displayName}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Users className="w-16 h-16 text-violet-300" />
                      </div>
                    )}
                  </div>
                  <Badge className="absolute top-3 left-3 bg-white/90 text-violet-700">
                    {category}
                  </Badge>
                  {profile.verification_status === "verified" && (
                    <Badge className="absolute top-3 right-3 bg-blue-500 text-white text-xs flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Verified
                    </Badge>
                  )}
                  {profile.is_represented && profile.represented_by && (
                    <Badge className="absolute bottom-3 left-3 bg-purple-500 text-white text-xs">
                      Rep: {profile.represented_by.name}
                    </Badge>
                  )}
                </div>
                <CardContent className="p-4">
                  <h3 
                    className="font-display text-lg font-semibold text-gray-900 mb-1 cursor-pointer hover:text-violet-600"
                    onClick={() => navigate(`/profile/${profile.profile_id}`)}
                  >
                    {displayName}
                  </h3>
                  {location && (
                    <p className="text-gray-500 text-sm flex items-center gap-1 mb-2">
                      <MapPin className="w-3 h-3" /> {location}
                    </p>
                  )}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      <span className="font-medium">{(profile.average_rating || 0).toFixed(1)}</span>
                      <span className="text-gray-500 text-sm">({profile.total_ratings || 0})</span>
                    </div>
                    {skills.length > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        +{skills.length} skills
                      </Badge>
                    )}
                  </div>
                  {/* Chat CTA Button */}
                  <Button 
                    size="sm" 
                    className="w-full bg-violet-600 hover:bg-violet-700"
                    onClick={(e) => {
                      e.stopPropagation();
                      const token = localStorage.getItem("token");
                      if (!token) {
                        navigate("/login", { state: { from: `/profile/${profile.profile_id}` } });
                      } else {
                        // Use user_id from profile for chat
                        navigate(`/chat?userId=${profile.user_id}`, { 
                          state: { targetUser: { name: displayName, user_id: profile.user_id } } 
                        });
                      }
                    }}
                    data-testid={`chat-btn-${profile.profile_id}`}
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Chat with {displayName?.split(' ')[0]}
                  </Button>
                </CardContent>
              </Card>
            )})}
          </div>
        ) : (
          <div className="space-y-4">
            {displayProfiles.map(profile => {
              const personal = profile.personal_info || {};
              const professional = profile.professional_info || {};
              const portfolio = profile.portfolio || {};
              const profilePhoto = portfolio.profile_photo || (portfolio.additional_photos || [])[0] || (profile.photos || [])[0];
              const displayName = personal.display_name || personal.full_name || profile.name;
              const category = professional.primary_category || profile.category;
              const location = personal.city ? `${personal.city}${personal.country ? `, ${personal.country}` : ''}` : profile.location;
              const skills = professional.skills || profile.skills || [];
              
              return (
              <Card 
                key={profile.profile_id} 
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => navigate(`/profile/${profile.profile_id}`)}
                data-testid={`profile-list-${profile.profile_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    <div className="talent-thumb-container w-24 h-32 rounded-lg flex-shrink-0">
                      {profilePhoto ? (
                        <img 
                          src={profilePhoto} 
                          alt={displayName}
                          className="rounded-lg"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Users className="w-8 h-8 text-gray-300" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-display text-lg font-semibold text-gray-900">
                            {displayName}
                          </h3>
                          <p className="text-violet-600 font-medium text-sm">{category}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                          <span className="font-medium">{(profile.average_rating || 0).toFixed(1)}</span>
                        </div>
                      </div>
                      {location && (
                        <p className="text-gray-500 text-sm flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" /> {location}
                        </p>
                      )}
                      {skills.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {skills.slice(0, 4).map((skill, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">
                              {skill}
                            </Badge>
                          ))}
                          {skills.length > 4 && (
                            <Badge variant="secondary" className="text-xs">
                              +{skills.length - 4}
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )})}
          </div>
        )}
        
        {/* Sidebar Ad */}
        <div className="mt-8">
          <AdvertisementBanner placement="discovery" format="banner" />
        </div>
      </main>
      
      {/* WhatsApp Support */}
      <WhatsAppSupport />
    </div>
  );
}
